/**
 * 🌌 PASSIONS SETUP MODAL - Étape 2 du flux d'inscription Heartbeats
 * 
 * "La loi d'attraction ne ment jamais... Les âmes qui vibrent 
 *  à la même fréquence finissent toujours par se trouver." 💜
 * 
 * Cette étape SÉPARÉE permet aux utilisateurs de répondre aux questions
 * sur leurs passions profondes APRÈS avoir créé leur profil de base.
 * 
 * ⚠️ L'utilisateur DOIT répondre à 100% des questions pour débloquer l'app !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Sparkles, Star, Check, X, ChevronRight, 
  Leaf, BookOpen, Film, Globe, Music, Palette, Lock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '../ui/dialog';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// Import des composants Soul existants
import { PassionSelector, SoulQuestionsWizard, PASSION_CATEGORIES, PASSIONS, SOUL_QUESTIONS } from './soul/SoulPassions';

// Nombre total de questions requises
const TOTAL_QUESTIONS = SOUL_QUESTIONS?.length || 5;

function PassionsSetupModal({ 
  isOpen, 
  onClose, 
  onSave, 
  existingPassions = {}, 
  existingSoulAnswers = {},
  isDarkMode = true,
  displayName = 'toi'
}) {
  const [passions, setPassions] = useState(existingPassions);
  const [soulAnswers, setSoulAnswers] = useState(existingSoulAnswers);
  const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'passions', 'questions', 'complete'
  
  // Réinitialiser l'état quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setPassions(existingPassions);
      setSoulAnswers(existingSoulAnswers);
      // Si déjà complété, aller directement à 'complete'
      const totalPassions = Object.values(existingPassions).reduce((acc, arr) => acc + (arr?.length || 0), 0);
      const totalAnswers = Object.keys(existingSoulAnswers).length;
      if (totalPassions >= 3 && totalAnswers >= TOTAL_QUESTIONS) {
        setCurrentStep('complete');
      } else {
        setCurrentStep('intro');
      }
    }
  }, [isOpen, existingPassions, existingSoulAnswers]);
  
  // Calculer le nombre total de passions sélectionnées
  const getTotalPassions = () => {
    return Object.values(passions).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  };
  
  // Calculer le nombre de questions répondues
  const getAnsweredQuestions = () => {
    return Object.keys(soulAnswers).length;
  };
  
  // Vérifier si toutes les questions sont répondues (100%)
  const allQuestionsAnswered = () => {
    return getAnsweredQuestions() >= TOTAL_QUESTIONS;
  };

  const handleComplete = () => {
    // Vérification finale
    if (getTotalPassions() < 3) {
      toast.error('Sélectionne au moins 3 passions 💜');
      setCurrentStep('passions');
      return;
    }
    
    if (!allQuestionsAnswered()) {
      toast.error(`Réponds à toutes les questions (${getAnsweredQuestions()}/${TOTAL_QUESTIONS}) 🎯`);
      setCurrentStep('questions');
      return;
    }
    
    // 🎉 Confettis de célébration !
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 },
      colors: ['#a855f7', '#ec4899', '#f43f5e', '#fbbf24']
    });
    
    onSave({ passions, soulAnswers });
    toast.success('🌟 Profil complet ! L\'algorithme va trouver tes matchs parfaits !');
  };

  // Empêcher le skip - Les passions sont OBLIGATOIRES
  const handleSkip = () => {
    toast.info('💜 Complete ton profil pour accéder à Heartbeats !', {
      description: `${TOTAL_QUESTIONS - getAnsweredQuestions()} questions restantes`
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {
      // Empêcher la fermeture si pas complété
      if (!allQuestionsAnswered() || getTotalPassions() < 3) {
        toast.info('🔒 Complete ton profil pour continuer !');
        return;
      }
      onClose();
    }}>
      <DialogContent 
        className={`sm:max-w-lg ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
        style={{
          maxHeight: 'calc(100dvh - 2rem)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* ÉTAPE INTRO */}
        {currentStep === 'intro' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">🌌</div>
            <DialogHeader>
              <DialogTitle className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Tes Passions Profondes
              </DialogTitle>
            </DialogHeader>
            
            <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Hé {displayName} ! 👋
            </p>
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Maintenant qu'on a ton joli profil, on va passer aux choses sérieuses...
            </p>
            
            <div className={`mt-6 p-4 rounded-xl ${isDarkMode ? 'bg-purple-900/30 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                <span className="text-lg">✨</span> Ces {TOTAL_QUESTIONS} questions permettent à notre algorithme de te trouver des personnes qui vibrent vraiment comme toi !
              </p>
            </div>
            
            {/* Indicateur de progression */}
            <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Progression</span>
                <span className={`font-bold ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                  {getAnsweredQuestions()}/{TOTAL_QUESTIONS} questions
                </span>
              </div>
              <div className={`h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${(getAnsweredQuestions() / TOTAL_QUESTIONS) * 100}%` }}
                />
              </div>
            </div>
            
            <div className="flex flex-col gap-3 mt-8">
              <Button
                onClick={() => setCurrentStep('passions')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 py-6 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                C'est parti ! 💜
              </Button>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Lock className="w-3 h-3 inline mr-1" />
                Obligatoire pour débloquer Heartbeats
              </p>
            </div>
          </motion.div>
        )}

        {/* ÉTAPE PASSIONS */}
        {currentStep === 'passions' && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="py-4"
          >
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <Heart className="w-6 h-6 text-pink-500" />
                Qu'est-ce qui te fait vibrer ?
              </DialogTitle>
            </DialogHeader>
            
            <p className={`text-sm mt-2 mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Sélectionne au moins 3 passions qui te correspondent vraiment 🎯
            </p>

            <PassionSelector
              selectedPassions={passions}
              onPassionsChange={(newPassions) => {
                setPassions(newPassions);
              }}
              isDarkMode={isDarkMode}
            />
            
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => setCurrentStep('intro')}
                className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}`}
              >
                ← Retour
              </button>
              
              <div className="flex items-center gap-3">
                <span className={`text-sm ${getTotalPassions() >= 3 ? 'text-green-400' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {getTotalPassions() >= 3 ? '✓' : ''} {getTotalPassions()}/3 minimum
                </span>
                <Button
                  onClick={() => setCurrentStep('questions')}
                  disabled={getTotalPassions() < 3}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  Continuer <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ÉTAPE QUESTIONS - 100% OBLIGATOIRE */}
        {currentStep === 'questions' && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="py-4"
          >
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <Star className="w-6 h-6 text-yellow-500" />
                Questions de l'âme 🔮
              </DialogTitle>
            </DialogHeader>
            
            <p className={`text-sm mt-2 mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {allQuestionsAnswered() 
                ? '✅ Toutes les questions sont répondues !' 
                : `Réponds aux ${TOTAL_QUESTIONS} questions pour un matching optimal !`}
            </p>

            <SoulQuestionsWizard
              answers={soulAnswers}
              onAnswersChange={(newAnswers) => {
                setSoulAnswers(newAnswers);
                // Si toutes les questions sont répondues, passer automatiquement à complete
                if (Object.keys(newAnswers).length >= TOTAL_QUESTIONS) {
                  setTimeout(() => setCurrentStep('complete'), 500);
                }
              }}
              isDarkMode={isDarkMode}
              onComplete={(finalAnswers) => {
                setSoulAnswers(finalAnswers);
                setCurrentStep('complete');
              }}
            />
            
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => setCurrentStep('passions')}
                className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}`}
              >
                ← Retour aux passions
              </button>
              
              {allQuestionsAnswered() && (
                <Button
                  onClick={() => setCurrentStep('complete')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  <Check className="w-4 h-4 mr-1" /> Voir le résumé
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* ÉTAPE COMPLETE */}
        {currentStep === 'complete' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="text-6xl mb-4">🎉</div>
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Bravo {displayName} !
            </h2>
            <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Ton profil est maintenant complet à 100% ! 
            </p>
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Notre algorithme va te trouver des personnes qui vibrent exactement comme toi 💜
            </p>
            
            <div className={`mt-6 p-4 rounded-xl ${isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                ✅ {getTotalPassions()} passions sélectionnées
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                ✅ {getAnsweredQuestions()}/{TOTAL_QUESTIONS} questions répondues (100%)
              </p>
            </div>
            
            {/* Indicateur algorithme */}
            <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/20 border border-purple-500/20' : 'bg-purple-50'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                🧠 L'algorithme Soul™ utilise tes {getTotalPassions()} passions + {getAnsweredQuestions()} réponses pour calculer ta compatibilité avec chaque profil !
              </p>
            </div>
            
            <Button
              onClick={handleComplete}
              className="mt-8 w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-6 text-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Découvrir mes matchs ! 🚀
            </Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PassionsSetupModal;
